import { AlertCircle, Check, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PasswordRequirementsChecklist } from '@/components/auth/PasswordRequirementsChecklist';
import { PasswordStrengthMeter } from '@/components/auth/PasswordStrengthMeter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TurnstileWidget } from '@/features/book/components/personen/TurnstileWidget';
import { phCapture } from '@/lib/analytics/posthog';
import { fireGa4SignUp } from '@/lib/analytics/gtag';
import { buildMetaAttributionPayload } from '@/lib/analytics/meta-payload';
import { ApiError, NetworkError } from '@/lib/api-client';
import { LEGAL_URLS } from '@/lib/config';
import { isStrongPassword } from '@/lib/password-rules';
import { computePostAuthRedirect } from '@/lib/post-auth-redirect';
import { readStoredUtms } from '@/lib/utm';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

type FormError = 'duplicateEmail' | 'generic' | 'network' | 'legal' | 'prelaunchLocked';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SignupRoute() {
  const navigate = useNavigate();
  const register = useAuthStore((s) => s.register);
  const status = useAuthStore((s) => s.status);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  // Bestandskunden-Hinweis (§ 7 Abs. 3 UWG). Pre-checked by default;
  // state tracks whether user ACCEPTS (= checked). Submitted as
  // `marketingObjection = !marketingAccepted`.
  const [marketingAccepted, setMarketingAccepted] = useState(true);
  // §A10 Turnstile — required on the backend RegisterDto since
  // sister-session commit b01a701. Same widget the book-flow
  // LightGateOverlay uses (2026-08-07 hotfix — audio signup was
  // 400ing on every attempt until this landed). See tracker
  // `[audio-signup-turnstile-not-wired]`.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordConfirmError, setPasswordConfirmError] = useState<string | null>(null);
  const [formError, setFormError] = useState<FormError | null>(null);

  // Double-submit guard (2026-10-30). The `status === 'authenticating'`
  // flag only flips INSIDE `authStore.register()`, so a rapid double
  // tap or submit + Enter within one tick can land two POSTs before
  // the store state propagates. The ref gates the handler
  // synchronously; the mirrored state drives the button disable.
  const authSubmittingRef = useRef(false);
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const isSubmitting = status === 'authenticating' || authSubmitting;

  const validateEmail = (value: string): string | null => {
    if (!value.trim()) return STRINGS.auth.errors.emailRequired;
    if (!EMAIL_RE.test(value)) return STRINGS.auth.errors.emailInvalid;
    return null;
  };

  // Rules mirror RegisterDto (src/auth/dto/register.dto.ts); shared
  // regex source in `@/lib/password-rules` keeps this validator,
  // PasswordRequirementsChecklist, and the Light-Gate pre-flight
  // in lockstep.
  const validatePassword = (value: string): string | null => {
    if (!value.trim()) return STRINGS.auth.errors.passwordRequired;
    if (value.length < 8) return STRINGS.auth.errors.passwordTooShort;
    if (!isStrongPassword(value)) {
      return STRINGS.auth.errors.passwordRequirementsNotMet;
    }
    return null;
  };

  const validateConfirm = (value: string, target: string): string | null => {
    if (!value) return STRINGS.auth.errors.passwordConfirmRequired;
    if (value !== target) return STRINGS.auth.errors.passwordMismatch;
    return null;
  };

  // Live-derived validity, computed on every render from the SAME validators
  // and consent gate that handleSubmit enforces — used ONLY to drive the
  // submit-button disabled state and the blocking-reason hint below. It never
  // sets the red *Error state, so inline field errors still surface on
  // blur/submit only (no per-keystroke red).
  const emailErrLive = validateEmail(email);
  const pwErrLive = validatePassword(password);
  const confirmErrLive = validateConfirm(passwordConfirm, password);
  const isValid =
    !emailErrLive && !pwErrLive && !confirmErrLive && consentChecked && turnstileToken !== null;
  // Single top blocking reason, in the submit-gate priority order. Reuses the
  // existing validator strings; consent falls back to the legal-required copy.
  const blockingReason =
    emailErrLive ??
    pwErrLive ??
    confirmErrLive ??
    (!consentChecked ? STRINGS.auth.errors.legalRequired : null);
  // Honest confirm affordance: green confirmation only once non-empty AND equal.
  const confirmMatches = passwordConfirm.length > 0 && passwordConfirm === password;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Double-submit guard (2026-10-30) — synchronous ref read so a
    // rapid double tap or submit + Enter within one tick cannot
    // reach `register()` twice.
    if (authSubmittingRef.current) return;

    const emailErr = validateEmail(email);
    const pwErr = validatePassword(password);
    const confirmErr = validateConfirm(passwordConfirm, password);
    setEmailError(emailErr);
    setPasswordError(pwErr);
    setPasswordConfirmError(confirmErr);
    if (emailErr || pwErr || confirmErr) return;

    if (!consentChecked) {
      setFormError('legal');
      return;
    }

    setFormError(null);
    // Marketing attribution (2026-07-17). Enrich signup_started with the
    // stored UTMs so bounces (started-not-completed) are still attributed
    // event-side. Empty object when the LP → app hop carried no UTMs.
    // Spread into a fresh object so the payload matches phCapture's
    // Record<string, unknown> signature (UtmRegisterPayload's named
    // optional fields don't carry an index signature).
    phCapture('signup_started', { ...readStoredUtms() });
    authSubmittingRef.current = true;
    setAuthSubmitting(true);
    try {
      // Turnstile token is enforced non-null by `isValid` (submit is
      // disabled until the widget provides one), so reaching this
      // branch guarantees a non-null token. The `?? ''` fallback is a
      // defensive belt only.
      await register({
        email,
        password,
        acceptedTerms: true,
        turnstileToken: turnstileToken ?? '',
        meta: buildMetaAttributionPayload(),
        marketingObjection: !marketingAccepted,
      });
      // GA4 funnel — sign_up with the entry point (Sebastian ruling
      // 2026-10-21). Direct signup lands via this route; method='other'
      // (figurenbild + book flows have their own light-gate overlays
      // with method='figurenbild' / 'book' respectively).
      fireGa4SignUp({ method: 'other' });
      const user = useAuthStore.getState().user;
      const target = user
        ? computePostAuthRedirect(user)
        : `/auth/confirm?email=${encodeURIComponent(email)}`;
      navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof NetworkError) {
        setFormError('network');
      } else if (err instanceof ApiError && err.errorCode === 'prelaunch_locked') {
        // Backend #42 (2026-05-01): prelaunch gate fires BEFORE the
        // existing httpStatus discriminator because 403 is shared with
        // email_not_verified — error_code is the only reliable signal.
        setFormError('prelaunchLocked');
      } else if (err instanceof ApiError && err.httpStatus === 409) {
        setFormError('duplicateEmail');
      } else {
        setFormError('generic');
      }
    } finally {
      authSubmittingRef.current = false;
      setAuthSubmitting(false);
    }
  };

  const errorMessage =
    formError === 'duplicateEmail'
      ? STRINGS.auth.errors.duplicateEmail
      : formError === 'legal'
        ? STRINGS.auth.errors.legalRequired
        : formError === 'network'
          ? STRINGS.auth.errors.networkError
          : formError === 'prelaunchLocked'
            ? STRINGS.auth.errors.prelaunchLocked
            : formError === 'generic'
              ? STRINGS.auth.errors.genericBackend
              : null;

  return (
    <>
      <h1 className="text-[22px] font-semibold text-dream-white mb-6">
        {STRINGS.auth.signup.title}
      </h1>

      {errorMessage && (
        <div
          role="alert"
          className="flex items-start gap-2 mb-4 p-3 rounded-md bg-[rgba(226,75,74,0.15)] border border-[rgba(226,75,74,0.4)] text-[#FECACA] text-sm"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            {errorMessage}
            {formError === 'duplicateEmail' && (
              <>
                {' '}
                <Link to="/auth/login" className="underline font-medium">
                  {STRINGS.auth.signup.loginLink}
                </Link>
              </>
            )}
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="signup-email" className="text-sm text-dream-white">
            {STRINGS.auth.signup.emailLabel}
          </Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
            }}
            onBlur={() => setEmailError(validateEmail(email))}
            aria-invalid={Boolean(emailError)}
            aria-describedby={emailError ? 'signup-email-error' : undefined}
            className="h-11 bg-dream-white text-dream-navy"
          />
          {emailError && (
            <p id="signup-email-error" className="text-xs text-[color:var(--color-error)]">
              {emailError}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-password" className="text-sm text-dream-white">
            {STRINGS.auth.signup.passwordLabel}
          </Label>
          <div className="relative">
            <Input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError(null);
              }}
              onBlur={() => setPasswordError(validatePassword(password))}
              aria-invalid={Boolean(passwordError)}
              aria-describedby={passwordError ? 'signup-password-error' : undefined}
              className="h-11 pr-10 bg-dream-white text-dream-navy"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={
                showPassword ? STRINGS.auth.login.hidePassword : STRINGS.auth.login.showPassword
              }
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-dream-navy transition-colors bg-dream-white"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {passwordError ? (
            <p id="signup-password-error" className="text-xs text-[color:var(--color-error)]">
              {passwordError}
            </p>
          ) : (
            <PasswordStrengthMeter password={password} />
          )}
          <PasswordRequirementsChecklist password={password} tone="dark" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signup-password-confirm" className="text-sm text-dream-white">
            {STRINGS.auth.signup.passwordConfirmLabel}
          </Label>
          <div className="relative">
            <Input
              id="signup-password-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              value={passwordConfirm}
              onChange={(e) => {
                setPasswordConfirm(e.target.value);
                if (passwordConfirmError) setPasswordConfirmError(null);
              }}
              onBlur={() => setPasswordConfirmError(validateConfirm(passwordConfirm, password))}
              aria-invalid={Boolean(passwordConfirmError)}
              aria-describedby={passwordConfirmError ? 'signup-password-confirm-error' : undefined}
              className="h-11 pr-10 bg-dream-white text-dream-navy"
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              aria-label={
                showConfirm ? STRINGS.auth.login.hidePassword : STRINGS.auth.login.showPassword
              }
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-dream-navy transition-colors bg-dream-white"
            >
              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {passwordConfirmError ? (
            <p
              id="signup-password-confirm-error"
              className="text-xs text-[color:var(--color-error)]"
            >
              {passwordConfirmError}
            </p>
          ) : (
            confirmMatches && (
              <p
                className="flex items-center gap-1 text-xs text-[color:var(--color-success)]"
                aria-live="polite"
              >
                <Check className="w-3.5 h-3.5 shrink-0" />
                {STRINGS.auth.signup.passwordMatch}
              </p>
            )
          )}
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => {
              setConsentChecked(e.target.checked);
              if (formError === 'legal') setFormError(null);
            }}
            className="mt-0.5 w-4 h-4 accent-dream-gold shrink-0 cursor-pointer"
            aria-describedby={formError === 'legal' ? 'signup-legal-error' : undefined}
          />
          <span className="text-xs text-dream-white/90 leading-relaxed">
            {STRINGS.auth.signup.legalCheckboxBefore}
            <a
              href={LEGAL_URLS.terms}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-dream-white"
              onClick={(e) => e.stopPropagation()}
            >
              {STRINGS.auth.signup.legalCheckboxTermsLink}
            </a>
            {STRINGS.auth.signup.legalCheckboxAnd}
            <a
              href={LEGAL_URLS.privacy}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-dream-white"
              onClick={(e) => e.stopPropagation()}
            >
              {STRINGS.auth.signup.legalCheckboxPrivacyLink}
            </a>
            {STRINGS.auth.signup.legalCheckboxAfter}
          </span>
        </label>

        {/*
          Bestandskunden-Hinweis (§ 7 Abs. 3 UWG). Pre-checked opt-out
          box + hint. Does not gate submit either way; `marketingObjection`
          is forwarded to the backend as `!marketingAccepted`.
        */}
        <div className="space-y-1">
          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={marketingAccepted}
              onChange={(e) => setMarketingAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-dream-gold shrink-0 cursor-pointer"
            />
            <span className="text-xs text-dream-white/90 leading-relaxed">
              {STRINGS.auth.marketingNotice.checkboxLabel}
            </span>
          </label>
          <p className="pl-[26px] text-[11px] text-dream-white/70 leading-relaxed">
            {STRINGS.auth.marketingNotice.hint}
          </p>
        </div>

        {/*
          §A10 Turnstile widget — required since sister-session commit
          b01a701 made turnstileToken a required RegisterDto field.
          Same component the book-flow LightGateOverlay uses; imported
          from features/book/ because that's where it lives today
          (widget authored for the LightGate, then extracted for reuse
          here in the same-day hotfix). Widget renders visibly under
          the dev key `1x00000000000000000000AA`; on real Managed
          production keys it is usually invisible.
        */}
        <TurnstileWidget onToken={setTurnstileToken} />

        <div>
          <Button
            type="submit"
            disabled={isSubmitting || !isValid}
            aria-disabled={isSubmitting || !isValid}
            className="w-full h-12 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSubmitting ? STRINGS.auth.signup.loadingState : STRINGS.auth.signup.submitButton}
          </Button>
          {!isSubmitting && !isValid && blockingReason && (
            <p className="mt-2 text-xs text-dream-white/70" aria-live="polite">
              {blockingReason}
            </p>
          )}
        </div>
      </form>

      <div className="mt-6 pt-6 border-t border-white/10 text-center text-sm">
        <span className="text-dream-white/70">{STRINGS.auth.signup.hasAccount}</span>{' '}
        <Link
          to="/auth/login"
          className="text-dream-white font-medium underline hover:text-dream-gold"
        >
          {STRINGS.auth.signup.loginLink}
        </Link>
      </div>
    </>
  );
}
